/**
 * Visual map editor — drop slots from a palette onto an uploaded zone map.
 *
 * Endpoints:
 *   - POST   /v1/parking/zones/:id/map-image        (multipart upload)
 *   - GET    /v1/parking/zones/:id/map-image        (binary stream, auth-gated)
 *   - GET    /v1/parking/zones/:id/placements       (list)
 *   - PUT    /v1/parking/zones/:id/placements       (bulk upsert)
 *   - DELETE /v1/parking/placements/:id
 *
 * Interaction model:
 *   - Map image is fetched as a blob via the auth-gated GET endpoint and
 *     rendered as a CSS-positioned `<img>` background. Slots are absolutely
 *     positioned overlays expressed in % of map width/height so the layout
 *     stays correct across viewport sizes.
 *   - Left panel ≈80% wide hosts the map + overlays.
 *   - Right panel ≈20% wide lists slots from this zone that don't yet have a
 *     placement; drag one onto the map to create a placement at the drop %.
 *   - Click an existing placement → mini-toolbar (delete / shape / rotate).
 *   - "Save layout" PUTs the working set in one call.
 *
 * State:
 *   - `placements` is the local working set; mutations diff against the
 *     server-fetched baseline on save. New placements use temporary IDs
 *     prefixed `tmp-` which the server discards (it allocates uuid). Local
 *     deletes go to a `deleted` set so the bulk PUT can carry tombstones —
 *     but the contract is upsert-only, so we fall back to the per-id
 *     DELETE endpoint for removals.
 *
 * Drag implementation is mouse-event based (no react-draggable dep) — the
 * primitive is small and we already control the surrounding container.
 *
 * Live status overlay: each placed slot tile is coloured by ParkingSlotStatus
 * via the sibling-owned `/parking/zones/:id/slots` listing. We tolerate a
 * 404 from that endpoint while the sibling agent's API hasn't landed.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Save,
  Upload,
  Trash2,
  RotateCw,
  Square,
  Circle as CircleIcon,
  ImagePlus,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { api, get, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { useParkingRealtime } from '@/hooks/useParkingRealtime';
import type {
  ParkingZone,
  ParkingSlot,
  ParkingSlotStatus
} from './_parking-types';
import { STATUS_LABEL } from './_parking-types';

// ─── types ─────────────────────────────────────────────────

type PlacementShape = 'rect' | 'circle';

interface Placement {
  id: string;
  slot_id: string;
  x_pct: number;
  y_pct: number;
  rotation_deg: number;
  shape: PlacementShape;
  width_pct: number;
  height_pct: number;
}

// Default tile size as a % of map dimensions — ~6% across.
const DEFAULT_WIDTH = 6;
const DEFAULT_HEIGHT = 4;

// Visual status palette.
const STATUS_COLOR: Record<ParkingSlotStatus, string> = {
  available: 'bg-vt-safe/80 ring-vt-safe',
  allocated: 'bg-vt-info/80 ring-vt-info',
  reserved: 'bg-vt-warn/80 ring-vt-warn',
  blocked: 'bg-vt-text-muted/80 ring-vt-text-muted'
};

// ─── helpers ───────────────────────────────────────────────

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function isTempId(id: string): boolean {
  return id.startsWith('tmp-');
}

// ─── page ──────────────────────────────────────────────────

export function ParkingMapPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { zoneId } = useParams({ strict: false }) as { zoneId: string };

  // Zone meta (for header). Tolerates 404 while sibling endpoint is in-flight.
  const zoneQuery = useQuery({
    queryKey: ['parking', 'zone', zoneId],
    queryFn: () => get<ParkingZone>(`/parking/zones/${zoneId}`),
    retry: false
  });

  // Slot listing — we only need slots without placements for the palette,
  // plus status for the overlay colouring. Sibling owns this endpoint.
  const slotsQuery = useQuery({
    queryKey: ['parking', 'slots', { zoneId }],
    queryFn: () =>
      get<{ items: ParkingSlot[] }>(`/parking/slots?zone_id=${zoneId}&limit=500`),
    retry: false
  });

  // Placements — our authoritative source for the canvas.
  const placementsQuery = useQuery({
    queryKey: ['parking', 'placements', zoneId],
    queryFn: () => get<Placement[]>(`/parking/zones/${zoneId}/placements`),
    retry: false
  });

  // Map image — auth-gated blob → object URL.
  const [mapBlobUrl, setMapBlobUrl] = useState<string | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapMissing, setMapMissing] = useState(false);

  const loadMapImage = useCallback(() => {
    let revoked = false;
    let created: string | null = null;
    setMapLoading(true);
    setMapMissing(false);
    api
      .get<Blob>(`/parking/zones/${zoneId}/map-image`, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        created = URL.createObjectURL(res.data);
        setMapBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return created;
        });
        setMapLoading(false);
      })
      .catch(() => {
        if (revoked) return;
        setMapMissing(true);
        setMapLoading(false);
      });
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [zoneId]);

  useEffect(() => {
    const cleanup = loadMapImage();
    return cleanup;
  }, [loadMapImage]);

  // Local working set — seeded from server response, mutated by drag/drop.
  const [working, setWorking] = useState<Placement[]>([]);
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (placementsQuery.data) {
      setWorking(placementsQuery.data);
      setDirty(false);
    }
  }, [placementsQuery.data]);

  useParkingRealtime({
    onSlotOccupied: () => {
      void queryClient.invalidateQueries({ queryKey: ['parking', 'slots', { zoneId }] });
    }
  });

  // Live status lookup.
  const slotsById = useMemo(() => {
    const m = new Map<string, ParkingSlot>();
    for (const s of slotsQuery.data?.items ?? []) m.set(s.id, s);
    return m;
  }, [slotsQuery.data]);

  const placedSlotIds = useMemo(
    () => new Set(working.map((p) => p.slot_id)),
    [working]
  );

  // Palette = zone slots that don't currently have a placement.
  const palette = useMemo(
    () => (slotsQuery.data?.items ?? []).filter((s) => !placedSlotIds.has(s.id)),
    [slotsQuery.data, placedSlotIds]
  );

  // ─── map drag handlers ──────────────────────────────────

  const mapRef = useRef<HTMLDivElement | null>(null);
  const dragSlotIdRef = useRef<string | null>(null);
  const dragPlacementRef = useRef<{ id: string; offsetX: number; offsetY: number } | null>(null);

  const pctFromEvent = useCallback((e: React.MouseEvent | MouseEvent): { x: number; y: number } | null => {
    const el = mapRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const x = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);
    return { x, y };
  }, []);

  const handlePaletteDragStart = (slotId: string) => (e: React.DragEvent): void => {
    dragSlotIdRef.current = slotId;
    e.dataTransfer.effectAllowed = 'copyMove';
    e.dataTransfer.setData('text/plain', slotId);
  };

  const handleMapDragOver = (e: React.DragEvent): void => {
    if (dragSlotIdRef.current) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleMapDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    const slotId = dragSlotIdRef.current ?? e.dataTransfer.getData('text/plain');
    dragSlotIdRef.current = null;
    if (!slotId) return;
    const pos = pctFromEvent(e);
    if (!pos) return;
    setWorking((prev) => {
      // Prevent double-placement defensively.
      if (prev.some((p) => p.slot_id === slotId)) return prev;
      return [
        ...prev,
        {
          id: `tmp-${slotId}-${Date.now()}`,
          slot_id: slotId,
          x_pct: pos.x,
          y_pct: pos.y,
          rotation_deg: 0,
          shape: 'rect',
          width_pct: DEFAULT_WIDTH,
          height_pct: DEFAULT_HEIGHT
        }
      ];
    });
    setDirty(true);
  };

  // Existing-placement drag (move) — handled as plain mouse events because
  // HTML5 native DnD on overlaid absolute elements is finicky.
  const onPlacementMouseDown = (id: string) => (e: React.MouseEvent): void => {
    e.stopPropagation();
    const el = mapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const placement = working.find((p) => p.id === id);
    if (!placement) return;
    const placementX = (placement.x_pct / 100) * rect.width;
    const placementY = (placement.y_pct / 100) * rect.height;
    dragPlacementRef.current = {
      id,
      offsetX: e.clientX - rect.left - placementX,
      offsetY: e.clientY - rect.top - placementY
    };
    setSelected(id);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent): void => {
      const drag = dragPlacementRef.current;
      const el = mapRef.current;
      if (!drag || !el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const x = ((e.clientX - rect.left - drag.offsetX) / rect.width) * 100;
      const y = ((e.clientY - rect.top - drag.offsetY) / rect.height) * 100;
      setWorking((prev) =>
        prev.map((p) => (p.id === drag.id ? { ...p, x_pct: clamp(x, 0, 100), y_pct: clamp(y, 0, 100) } : p))
      );
    };
    const onUp = (): void => {
      if (dragPlacementRef.current) {
        dragPlacementRef.current = null;
        setDirty(true);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // ─── placement mutations ────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: (payload: Placement[]) =>
      api.put<Placement[]>(`/parking/zones/${zoneId}/placements`, payload).then((r) => r.data),
    onSuccess: () => {
      toast.success('Layout saved');
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['parking', 'placements', zoneId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted: boolean }>(`/parking/placements/${id}`).then((r) => r.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['parking', 'placements', zoneId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const onDeletePlacement = (id: string): void => {
    setWorking((prev) => prev.filter((p) => p.id !== id));
    if (!isTempId(id)) {
      deleteMutation.mutate(id);
    }
    setSelected(null);
    setDirty(true);
  };

  const onToggleShape = (id: string): void => {
    setWorking((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, shape: p.shape === 'rect' ? 'circle' : 'rect' } : p
      )
    );
    setDirty(true);
  };

  const onRotate = (id: string): void => {
    setWorking((prev) =>
      prev.map((p) => (p.id === id ? { ...p, rotation_deg: (p.rotation_deg + 15) % 360 } : p))
    );
    setDirty(true);
  };

  const onSave = (): void => {
    // Strip temp ids before sending — server allocates them.
    const payload = working.map((p) => (isTempId(p.id) ? { ...p, id: undefined as unknown as string } : p));
    saveMutation.mutate(payload);
  };

  // ─── image upload ───────────────────────────────────────

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api
        .post<{ ok: boolean }>(`/parking/zones/${zoneId}/map-image`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then((r) => r.data);
    },
    onSuccess: () => {
      toast.success('Map image uploaded');
      // Reload the binary endpoint so the new image renders immediately.
      loadMapImage();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const onPickFile = (): void => {
    fileInputRef.current?.click();
  };
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    uploadMutation.mutate(file);
  };

  // ─── render ─────────────────────────────────────────────

  return (
    <PageHeader
      title={zoneQuery.data ? `Map · ${zoneQuery.data.name}` : 'Zone map'}
      description="Place each slot on the zone diagram. Drag from the palette to add, drag tiles to reposition."
      actions={
        <div className="flex items-center gap-vt-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/parking">
              <ArrowLeft className="h-4 w-4" />
              Back to parking
            </Link>
          </Button>
          <Button variant="secondary" size="sm" onClick={onPickFile} loading={uploadMutation.isPending}>
            <Upload className="h-4 w-4" />
            Upload map image
          </Button>
          <Button onClick={onSave} disabled={!dirty} loading={saveMutation.isPending}>
            <Save className="h-4 w-4" />
            Save layout
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFileChange}
          />
        </div>
      }
    >
      {/* Breadcrumb-ish */}
      <div className="mb-vt-4 text-vt-12 text-vt-text-muted">
        <Link to="/parking" className="hover:text-vt-primary">
          Parking
        </Link>
        {' · '}
        <span>Zone {zoneQuery.data?.code ?? zoneId.slice(0, 6)}</span>
        {' · '}
        <span>Map editor</span>
      </div>

      <div className="grid grid-cols-1 gap-vt-4 lg:grid-cols-5">
        {/* ─── canvas ─── */}
        <div className="lg:col-span-4">
          <Card>
            <CardContent className="space-y-vt-3 p-vt-4">
              <div className="flex flex-wrap items-center justify-between gap-vt-2">
                <div className="flex flex-wrap items-center gap-vt-2">
                  <Badge variant="info">{working.length} placed</Badge>
                  <Badge variant="muted">{palette.length} unplaced</Badge>
                  {dirty ? <Badge variant="warn">Unsaved changes</Badge> : null}
                </div>
                <Legend />
              </div>

              <div
                ref={mapRef}
                onDragOver={handleMapDragOver}
                onDrop={handleMapDrop}
                onMouseDown={() => setSelected(null)}
                className="relative aspect-[16/9] w-full overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface-2"
                role="application"
                aria-label="Zone map editor canvas"
              >
                {mapLoading ? (
                  <Skeleton className="h-full w-full" />
                ) : mapBlobUrl ? (
                  <img
                    src={mapBlobUrl}
                    alt="Zone map"
                    draggable={false}
                    className="pointer-events-none h-full w-full object-contain"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center p-vt-6">
                    <EmptyState
                      icon={<ImagePlus className="h-7 w-7" />}
                      title={mapMissing ? 'No map image yet' : 'Map unavailable'}
                      description={
                        mapMissing
                          ? 'Upload a floorplan, satellite snapshot or hand-drawn diagram to anchor slot placements.'
                          : 'Could not load the zone map.'
                      }
                      action={
                        <Button onClick={onPickFile} loading={uploadMutation.isPending}>
                          <Upload className="h-4 w-4" />
                          Upload image
                        </Button>
                      }
                    />
                  </div>
                )}

                {/* Placement overlays */}
                {working.map((p) => {
                  const slot = slotsById.get(p.slot_id);
                  const status = slot?.status ?? 'blocked';
                  const isSelected = selected === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onMouseDown={onPlacementMouseDown(p.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(p.id);
                      }}
                      title={`${slot?.slot_number ?? 'Unlinked'} · ${STATUS_LABEL[status]}${
                        slot?.owner_unit?.number ? ` · Owner ${slot.owner_unit.number}` : ''
                      }`}
                      className={
                        `group absolute -translate-x-1/2 -translate-y-1/2 cursor-grab select-none ring-2 text-[10px] font-semibold !text-white shadow-vt-1 transition-shadow active:cursor-grabbing ${
                          STATUS_COLOR[status]
                        } ${
                          p.shape === 'circle' ? 'rounded-full' : 'rounded-vt-sm'
                        } ${isSelected ? 'shadow-vt-2 ring-vt-primary outline outline-2 outline-vt-primary' : ''}`
                      }
                      style={{
                        left: `${p.x_pct}%`,
                        top: `${p.y_pct}%`,
                        width: `${p.width_pct}%`,
                        height: `${p.height_pct}%`,
                        transform: `translate(-50%, -50%) rotate(${p.rotation_deg}deg)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <span className="px-1">{slot?.slot_number ?? '?'}</span>
                    </button>
                  );
                })}

                {/* Mini-toolbar for selected placement */}
                {selected ? (
                  <MiniToolbar
                    placement={working.find((p) => p.id === selected) ?? null}
                    onDelete={() => onDeletePlacement(selected)}
                    onToggleShape={() => onToggleShape(selected)}
                    onRotate={() => onRotate(selected)}
                  />
                ) : null}
              </div>

              <p className="text-vt-12 text-vt-text-muted">
                Tip: drag a slot tile from the right panel onto the map to place it. Click a tile to
                rotate, change shape or delete. Tile colour reflects current slot status.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* ─── palette ─── */}
        <div>
          <Card>
            <CardContent className="space-y-vt-2 p-vt-4">
              <div className="flex items-center gap-vt-2">
                <MapPin className="h-4 w-4 text-vt-text-muted" />
                <h2 className="text-vt-14 font-semibold text-vt-text">Unplaced slots</h2>
                <Badge variant="muted">{palette.length}</Badge>
              </div>
              {slotsQuery.isLoading ? (
                <Skeleton className="h-40 w-full" />
              ) : palette.length === 0 ? (
                <p className="py-vt-3 text-vt-13 text-vt-text-muted">
                  Every slot in this zone is on the map.
                </p>
              ) : (
                <ul className="max-h-[60vh] space-y-vt-1 overflow-y-auto pr-1">
                  {palette.map((slot) => (
                    <li
                      key={slot.id}
                      draggable
                      onDragStart={handlePaletteDragStart(slot.id)}
                      className="flex cursor-grab items-center justify-between gap-vt-2 rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13 transition-colors hover:border-vt-primary-400 hover:bg-vt-primary-100 active:cursor-grabbing"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium text-vt-text">{slot.slot_number}</div>
                        <div className="text-vt-11 text-vt-text-muted">
                          {slot.owner_unit?.number
                            ? `Owner ${slot.owner_unit.number}`
                            : 'Unassigned'}
                        </div>
                      </div>
                      <Badge variant="muted">{STATUS_LABEL[slot.status]}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PageHeader>
  );
}

function Legend(): JSX.Element {
  const items: Array<{ status: ParkingSlotStatus; label: string }> = [
    { status: 'available', label: 'Available' },
    { status: 'allocated', label: 'Allocated' },
    { status: 'reserved', label: 'Reserved' },
    { status: 'blocked', label: 'Blocked' }
  ];
  return (
    <div className="flex flex-wrap items-center gap-vt-2 text-vt-11 text-vt-text-muted">
      {items.map((it) => (
        <span key={it.status} className="inline-flex items-center gap-1">
          <span
            className={`inline-block h-2 w-2 rounded-sm ring-1 ring-inset ${STATUS_COLOR[it.status]}`}
            aria-hidden="true"
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function MiniToolbar({
  placement,
  onDelete,
  onToggleShape,
  onRotate
}: {
  placement: Placement | null;
  onDelete: () => void;
  onToggleShape: () => void;
  onRotate: () => void;
}): JSX.Element | null {
  if (!placement) return null;
  // Anchor near the placement; keep within map bounds.
  const left = clamp(placement.x_pct, 5, 90);
  const top = clamp(placement.y_pct - placement.height_pct / 2 - 4, 0, 90);
  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      className="pointer-events-auto absolute z-10 flex -translate-x-1/2 -translate-y-full items-center gap-1 rounded-vt-pill border border-vt-border bg-vt-surface px-vt-2 py-1 shadow-vt-2"
      style={{ left: `${left}%`, top: `${top}%` }}
    >
      <Button variant="ghost" size="sm" onClick={onToggleShape} title="Toggle shape">
        {placement.shape === 'rect' ? (
          <CircleIcon className="h-4 w-4" />
        ) : (
          <Square className="h-4 w-4" />
        )}
      </Button>
      <Button variant="ghost" size="sm" onClick={onRotate} title="Rotate 15°">
        <RotateCw className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={onDelete} title="Remove from map">
        <Trash2 className="h-4 w-4 text-vt-alert" />
      </Button>
    </div>
  );
}
